import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useAuth } from "../../../../../context/AuthContext";
import { API_BASE_URL, API_ROOT, EXPRESS_BASE_URL } from "../../../../../Helpers/apiConfig";

const EXPRESS_API = EXPRESS_BASE_URL;
const BASE_API = API_ROOT;

export const AdminMediaManagement = () => {
    const { user } = useAuth();
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [mediaList, setMediaList] = useState([]);
    const [loadingMedia, setLoadingMedia] = useState(false);

    // Kategori Modal State'leri
    const [showCatModal, setShowCatModal] = useState(false);
    const [catModalMode, setCatModalMode] = useState("create");
    const [editingCategory, setEditingCategory] = useState(null);
    const [catForm, setCatForm] = useState({ name: "", description: "", sortid: 0, category_id: null });

    // Medya Önizleme ve Düzenleme Modal State
    const [previewMedia, setPreviewMedia] = useState(null);
    const [editMediaForm, setEditMediaForm] = useState({ name: "", description: "" });
    const [isUpdatingMedia, setIsUpdatingMedia] = useState(false);

    // Toplu Yükleme Kuyruk Modal State
    const [uploadQueue, setUploadQueue] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [activeUploadType, setActiveUploadType] = useState("photo");

    // --- TAŞIMA (KLASÖR DEĞİŞTİRME) STATE'LERİ ---
    const [selectedMediaIds, setSelectedMediaIds] = useState([]);
    const [moveModalOpen, setMoveModalOpen] = useState(false);
    const [targetCategoryId, setTargetCategoryId] = useState("");
    const [isMoving, setIsMoving] = useState(false);

    // Taşıma Modalı için İkili Select ve Arama State'leri
    const [searchCategoryText, setSearchCategoryText] = useState("");
    const [selectedRootCatId, setSelectedRootCatId] = useState("");
    const [selectedSubCatId, setSelectedSubCatId] = useState("");
    
    const fileInputRef = useRef(null);
    const addMoreInputRef = useRef(null);
    const replacingIndexRef = useRef(null);

    const headers = {
        Authorization: `Bearer ${user?.token}`,
    };

    const fetchCategoriesTree = async () => {
        try {
            const res = await axios.get(`${EXPRESS_API}/api/categories/tree`);
            if (res.data.success) {
                setCategories(res.data.data);
            }
        } catch (err) {
            console.error("Kategoriler yüklenemedi", err);
        }
    };

    useEffect(() => {
        fetchCategoriesTree();
    }, []);

    const fetchMediaByCategory = async (catId) => {
        if (!catId) return;
        setLoadingMedia(true);
        setSelectedMediaIds([]);
        try {
            const res = await axios.get(`${EXPRESS_API}/api/media/bycategory/${catId}`);
            if (res.data.success) {
                setMediaList(res.data.data);
            }
        } catch (err) {
            console.error("Medya listelenemedi", err);
        } finally {
            setLoadingMedia(false);
        }
    };

    const handleSelectCategory = (cat) => {
        setSelectedCategory(cat);
        fetchMediaByCategory(cat.id);
    };

    const handleSaveCategory = async (e) => {
        e.preventDefault();
        try {
            if (catModalMode === "create") {
                await axios.post(`${EXPRESS_API}/api/categories`, catForm, { headers });
                Swal.fire("Başarılı", "Kategori oluşturuldu.", "success");
            } else {
                await axios.put(`${EXPRESS_API}/api/categories/${editingCategory.id}`, catForm, { headers });
                Swal.fire("Başarılı", "Kategori güncellendi.", "success");
            }
            setShowCatModal(false);
            fetchCategoriesTree();
        } catch (err) {
            Swal.fire("Hata", err.response?.data?.message || "Bir hata oluştu", "error");
        }
    };

    const handleDeleteCategory = async (cat, e) => {
        e.stopPropagation();
        const confirm = await Swal.fire({
            title: `"${cat.name}" kategorisini silmek istediğinize emin misiniz?`,
            text: "İçinde alt kategori veya medya varsa silinmeyecektir.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Evet, Sil",
            cancelButtonText: "Vazgeç"
        });

        if (!confirm.isConfirmed) return;

        try {
            const res = await axios.delete(`${EXPRESS_API}/api/categories/${cat.id}`, { headers });
            if (res.data.success) {
                Swal.fire("Başarılı", "Kategori silindi.", "success");
                if (selectedCategory?.id === cat.id) {
                    setSelectedCategory(null);
                    setMediaList([]);
                }
                fetchCategoriesTree();
            }
        } catch (err) {
            Swal.fire("Silinemedi", err.response?.data?.message || "Bu kategori alt kategori veya medya içeriyor.", "error");
        }
    };

    const handleDeleteMedia = async (mediaId, e) => {
        e.stopPropagation();
        const confirm = await Swal.fire({
            title: "Bu medyayı silmek istediğinize emin misiniz?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Evet, Sil",
            cancelButtonText: "Vazgeç"
        });

        if (!confirm.isConfirmed) return;

        try {
            const res = await axios.delete(`${EXPRESS_API}/api/media/${mediaId}`, { headers });
            if (res.data.success) {
                Swal.fire("Başarılı", "Medya silindi.", "success");
                setMediaList(prev => prev.filter(m => m.id !== mediaId));
                setSelectedMediaIds(prev => prev.filter(id => id !== mediaId));
            }
        } catch (err) {
            Swal.fire("Hata", "Medya silinirken bir hata oluştu.", "error");
        }
    };

    const handleUpdateMedia = async (e) => {
        e.preventDefault();
        if (!previewMedia) return;

        setIsUpdatingMedia(true);
        try {
            const payload = {
                category_id: previewMedia.category_id,
                type: previewMedia.type,
                name: editMediaForm.name,
                description: editMediaForm.description || null,
                url: previewMedia.url
            };

            const res = await axios.put(`${EXPRESS_API}/api/media/${previewMedia.id}`, payload, { headers });
            if (res.data.success) {
                Swal.fire({
                    title: "Başarılı!",
                    text: "Medya bilgileri güncellendi.",
                    icon: "success",
                    timer: 2000,
                    showConfirmButton: false
                });
                
                setMediaList(prev => prev.map(m => m.id === previewMedia.id ? { ...m, name: editMediaForm.name, description: editMediaForm.description } : m));
                setPreviewMedia(prev => ({ ...prev, name: editMediaForm.name, description: editMediaForm.description }));
            }
        } catch (err) {
            Swal.fire("Hata", err.response?.data?.message || "Güncelleme sırasında bir hata oluştu.", "error");
        } finally {
            setIsUpdatingMedia(false);
        }
    };

    const handleSelectAllCheckbox = (e) => {
        if (e.target.checked) {
            setSelectedMediaIds(mediaList.map(m => m.id));
        } else {
            setSelectedMediaIds([]);
        }
    };

    const handleCheckboxChange = (mediaId, e) => {
        e.stopPropagation();
        setSelectedMediaIds(prev => 
            prev.includes(mediaId) ? prev.filter(id => id !== mediaId) : [...prev, mediaId]
        );
    };

    // --- TAŞIMA İŞLEMİ ---
    const handleMoveMedias = async () => {
        const finalTargetId = selectedSubCatId || selectedRootCatId;
        if (!finalTargetId) {
            Swal.fire("Uyarı", "Lütfen hedef bir kategori seçiniz.", "warning");
            return;
        }
        if (selectedMediaIds.length === 0) return;

        setIsMoving(true);
        try {
            for (const mediaId of selectedMediaIds) {
                const mediaItem = mediaList.find(m => m.id === mediaId);
                if (!mediaItem) continue;

                const payload = {
                    category_id: Number(finalTargetId),
                    type: mediaItem.type,
                    name: mediaItem.name,
                    description: mediaItem.description || null,
                    url: mediaItem.url
                };

                await axios.put(`${EXPRESS_API}/api/media/${mediaId}`, payload, { headers });
            }

            Swal.fire("Başarılı", "Medya(lar) başarıyla taşındı.", "success");
            setMoveModalOpen(false);
            setSelectedMediaIds([]);
            setSelectedRootCatId("");
            setSelectedSubCatId("");
            setSearchCategoryText("");
            fetchMediaByCategory(selectedCategory.id);
        } catch (err) {
            Swal.fire("Hata", "Taşıma işlemi sırasında bir hata oluştu.", "error");
        } finally {
            setIsMoving(false);
        }
    };

    // Tüm kategorileri düzleştirilmiş şekilde arama için hazırlama
    const flattenCategories = (nodes, list = []) => {
        nodes.forEach(node => {
            list.push(node);
            if (node.children && node.children.length > 0) {
                flattenCategories(node.children, list);
            }
        });
        return list;
    };

    const triggerFileSelect = (mediaType) => {
        if (!selectedCategory) {
            Swal.fire("Uyarı", "Lütfen önce sol taraftan bir kategori seçiniz!", "warning");
            return;
        }
        setActiveUploadType(mediaType);
        if (fileInputRef.current) {
            fileInputRef.current.accept = 
                mediaType === "photo" ? "image/*" :
                mediaType === "video" ? "video/*" : "audio/*";
            fileInputRef.current.click();
        }
    };

    const handleFileChosen = (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const queueItems = files.map((file, index) => ({
            id: Date.now() + index,
            file: file,
            name: file.name.substring(0, file.name.lastIndexOf('.')) || file.name,
            description: "",
            status: "pending",
            errorMessage: "",
        }));

        setUploadQueue(queueItems);
        setUploadModalOpen(true);
        e.target.value = null;
    };

    const handleAddMoreFiles = (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const newItems = files.map((file, index) => ({
            id: Date.now() + index,
            file: file,
            name: file.name.substring(0, file.name.lastIndexOf('.')) || file.name,
            description: "",
            status: "pending",
            errorMessage: "",
        }));

        setUploadQueue(prev => [...prev, ...newItems]);
        e.target.value = null;
    };

    const handleReplaceFileChosen = (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0 || replacingIndexRef.current === null) return;

        const newFile = files[0];
        const idx = replacingIndexRef.current;

        setUploadQueue(prev => prev.map((item, i) => i === idx ? {
            ...item,
            file: newFile,
            name: newFile.name.substring(0, newFile.name.lastIndexOf('.')) || newFile.name,
            status: "pending",
            errorMessage: ""
        } : item));

        replacingIndexRef.current = null;
        e.target.value = null;
    };

    const handleRemoveFromQueue = (idx) => {
        setUploadQueue(prev => prev.filter((_, i) => i !== idx));
    };

    const startBatchUpload = async () => {
        setIsUploading(true);
        let updatedQueue = [...uploadQueue];

        for (let i = 0; i < updatedQueue.length; i++) {
            if (updatedQueue[i].status === "success") continue;

            updatedQueue[i].status = "uploading";
            updatedQueue[i].errorMessage = "";
            setUploadQueue([...updatedQueue]);

            try {
                const formData = new FormData();
                formData.append("FormFile", updatedQueue[i].file);

                const uploadRes = await axios.post(
                    `${API_BASE_URL}/admin/ContentFile/UploadFile`,
                    formData,
                    {
                        params: { mediaType: activeUploadType },
                        headers: {
                            ...headers,
                            "Content-Type": "multipart/form-data",
                        },
                    }
                );

                if (uploadRes.data && uploadRes.data.success === true && uploadRes.data.data?.path) {
                    let relativePath = uploadRes.data.data.path;
                    if (relativePath.startsWith("/")) {
                        relativePath = relativePath.substring(1);
                    }
                    const fullFileUrl = `${BASE_API}/${relativePath}`;
                    const typeNumber = activeUploadType === "photo" ? 1 : activeUploadType === "sound" ? 2 : 3;

                    const mediaPayload = {
                        category_id: selectedCategory.id,
                        type: typeNumber,
                        name: updatedQueue[i].name,
                        description: updatedQueue[i].description || null,
                        url: fullFileUrl,
                    };

                    await axios.post(`${EXPRESS_API}/api/media`, mediaPayload, { headers });

                    updatedQueue[i].status = "success";
                } else {
                    updatedQueue[i].status = "error";
                    updatedQueue[i].errorMessage = uploadRes.data?.message || "Yükleme başarısız oldu.";
                }
            } catch (err) {
                console.error("Yükleme hatası:", err);
                updatedQueue[i].status = "error";
                updatedQueue[i].errorMessage = err.response?.data?.message || err.message || "Sunucu hatası.";
            }
            setUploadQueue([...updatedQueue]);
        }

        setIsUploading(false);
        fetchMediaByCategory(selectedCategory.id);
    };

    const renderCategoryTree = (nodes) => {
        return nodes.map((cat) => {
            const isSelected = selectedCategory?.id === cat.id;
            return (
                <li key={cat.id} className="my-1">
                    <div 
                        className={`d-flex justify-content-between align-items-center p-2 rounded ${isSelected ? 'bg-primary text-white' : 'bg-light'}`}
                        style={{ cursor: "pointer" }}
                    >
                        <span onClick={() => handleSelectCategory(cat)} className="flex-grow-1">
                            📁 {cat.name}
                        </span>
                        <div>
                            <button 
                                className="btn btn-sm btn-link text-warning p-0 me-2"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setCatModalMode("edit");
                                    setEditingCategory(cat);
                                    setCatForm({ name: cat.name, description: cat.description || "", sortid: cat.sortid || 0, category_id: cat.category_id });
                                    setShowCatModal(true);
                                }}
                                title="Düzenle"
                            >
                                ✏️
                            </button>
                            <button 
                                className="btn btn-sm btn-link text-danger p-0"
                                onClick={(e) => handleDeleteCategory(cat, e)}
                                title="Kategoriyi Sil"
                            >
                                🗑️
                            </button>
                        </div>
                    </div>
                    {cat.children && cat.children.length > 0 && (
                        <ul className="ps-3 list-unstyled mt-1">
                            {renderCategoryTree(cat.children)}
                        </ul>
                    )}
                </li>
            );
        });
    };

    return (
        <div className="container-fluid vh-100 d-flex flex-column p-3 bg-light">
            <input type="file" ref={fileInputRef} style={{ display: "none" }} multiple onChange={handleFileChosen} />

            {/* ÜST KISIM */}
            <div className="row mb-3 bg-white p-3 shadow-sm rounded align-items-center">
                <div className="col-md-4">
                    <h4 className="m-0 text-primary fw-bold">Medya ve Kategori Yönetimi</h4>
                    <small className="text-muted">
                        {selectedCategory ? `Seçili Kategori: ${selectedCategory.name}` : "Lütfen bir kategori seçin"}
                    </small>
                </div>
                <div className="col-md-8 text-end">
                    <button className="btn btn-success me-2" onClick={() => triggerFileSelect("photo")} disabled={!selectedCategory}>
                        🖼️ Görsel Yükle
                    </button>
                    <button className="btn btn-warning me-2 text-dark" onClick={() => triggerFileSelect("sound")} disabled={!selectedCategory}>
                        🎵 Ses Yükle
                    </button>
                    <button className="btn btn-danger" onClick={() => triggerFileSelect("video")} disabled={!selectedCategory}>
                        🎥 Video Yükle
                    </button>
                </div>
            </div>

            {/* ANA İÇERİK */}
            <div className="row flex-grow-1 overflow-hidden g-3">
                {/* SOL PANEL: Kategoriler (%30) */}
                <div className="col-md-4 col-lg-3 h-100 d-flex flex-column bg-white shadow-sm rounded p-3">
                    <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                        <h5 className="m-0 fw-bold">Kategoriler</h5>
                        <button 
                            className="btn btn-sm btn-primary"
                            onClick={() => {
                                setCatModalMode("create");
                                setCatForm({ name: "", description: "", sortid: 0, category_id: null });
                                setShowCatModal(true);
                            }}
                        >
                            + Ana Kategori
                        </button>
                    </div>
                    <div className="flex-grow-1 overflow-auto">
                        {categories.length === 0 ? (
                            <p className="text-muted text-center mt-4">Kategori bulunamadı.</p>
                        ) : (
                            <ul className="list-unstyled">{renderCategoryTree(categories)}</ul>
                        )}
                    </div>
                </div>

                {/* SAĞ PANEL: Medya Kartları (%70) */}
                <div className="col-md-8 col-lg-9 h-100 d-flex flex-column bg-white shadow-sm rounded p-3">
                    <div className="border-bottom pb-2 mb-3 d-flex justify-content-between align-items-center">
                        <h5 className="m-0 fw-bold">Medya İçerikleri</h5>
                        
                        {selectedCategory && mediaList.length > 0 && (
                            <div className="d-flex align-items-center gap-3">
                                <div className="form-check m-0">
                                    <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        id="selectAllCheck"
                                        checked={selectedMediaIds.length === mediaList.length && mediaList.length > 0}
                                        onChange={handleSelectAllCheckbox}
                                    />
                                    <label className="form-check-label small fw-semibold" htmlFor="selectAllCheck">
                                        Tümünü Seç ({selectedMediaIds.length}/{mediaList.length})
                                    </label>
                                </div>
                                {selectedMediaIds.length > 0 && (
                                    <button 
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => {
                                            setSelectedRootCatId("");
                                            setSelectedSubCatId("");
                                            setSearchCategoryText("");
                                            setMoveModalOpen(true);
                                        }}
                                    >
                                        📁 Seçilenleri Başka Klasöre Taşı ({selectedMediaIds.length})
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="flex-grow-1 overflow-auto p-2" style={{ maxHeight: "calc(100vh - 200px)" }}>
                        {loadingMedia ? (
                            <div className="text-center mt-5">
                                <div className="spinner-border text-primary" role="status"></div>
                            </div>
                        ) : !selectedCategory ? (
                            <div className="text-center text-muted mt-5">
                                <h5>İçerikleri görmek için sol menüden bir kategori seçin.</h5>
                            </div>
                        ) : mediaList.length === 0 ? (
                            <div className="text-center text-muted mt-5">
                                <p>Bu kategoride henüz medya bulunmuyor.</p>
                            </div>
                        ) : (
                            <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-6 g-3">
                                {mediaList.map((media) => {
                                    const isChecked = selectedMediaIds.includes(media.id);
                                    return (
                                        <div key={media.id} className="col">
                                            <div 
                                                className={`card h-100 shadow-sm border-0 position-relative ${isChecked ? 'border-primary border-2' : ''}`} 
                                                onClick={() => {
                                                    setPreviewMedia(media);
                                                    setEditMediaForm({ name: media.name || "", description: media.description || "" });
                                                }}
                                                style={{ cursor: "pointer" }}
                                            >
                                                <div className="position-absolute top-0 start-0 m-1" style={{ zIndex: 2 }}>
                                                    <input 
                                                        type="checkbox" 
                                                        className="form-check-input shadow-sm"
                                                        checked={isChecked}
                                                        onChange={(e) => handleCheckboxChange(media.id, e)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </div>

                                                <button 
                                                    className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1 p-1"
                                                    style={{ fontSize: "0.7rem", zIndex: 2 }}
                                                    onClick={(e) => handleDeleteMedia(media.id, e)}
                                                    title="Medyayı Sil"
                                                >
                                                    ❌
                                                </button>

                                                <div className="bg-light d-flex align-items-center justify-content-center overflow-hidden" style={{ height: "110px" }}>
                                                    {media.type === 1 || media.type === "resim" ? (
                                                        <img src={media.url} alt={media.name} className="w-100 h-100 object-fit-cover" />
                                                    ) : media.type === 3 || media.type === "video" ? (
                                                        <video src={media.url} className="w-100 h-100 object-fit-cover" />
                                                    ) : (
                                                        <div className="fs-1">🎵</div>
                                                    )}
                                                </div>
                                                <div className="card-body p-2 text-center">
                                                    <p className="card-title small text-truncate m-0 fw-semibold" title={media.name}>
                                                        {media.name}
                                                    </p>
                                                    <span className="badge bg-secondary mt-1" style={{ fontSize: "0.65rem" }}>
                                                        {media.type}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* --- GELİŞMİŞ KLASÖR DEĞİŞTİR / TAŞIMA MODALI --- */}
            {moveModalOpen && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Medya(ları) Başka Klasöre Taşı</h5>
                                <button type="button" className="btn-close" onClick={() => setMoveModalOpen(false)}></button>
                            </div>
                            <div className="modal-body">
                                <p className="small text-muted mb-3">
                                    Seçilen <strong>{selectedMediaIds.length}</strong> adet medyayı taşımak için kategori arayabilir veya hiyerarşik olarak seçebilirsiniz:
                                </p>

                                {/* Harici Arama Textbox'ı */}
                                <div className="mb-3">
                                    <label className="form-label small fw-bold">🔍 Kategori İsmi Ara</label>
                                    <input 
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Kategori adı yazarak filtrele..."
                                        value={searchCategoryText}
                                        onChange={(e) => setSearchCategoryText(e.target.value)}
                                    />
                                </div>

                                {searchCategoryText.trim() !== "" ? (
                                    /* Arama Yapıldığında Filtrelenmiş Düz Liste */
                                    <div className="mb-3">
                                        <label className="form-label small">Arama Sonuçları:</label>
                                        <select 
                                            className="form-select"
                                            size="5"
                                            value={selectedSubCatId || selectedRootCatId}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setSelectedRootCatId(val);
                                                setSelectedSubCatId("");
                                            }}
                                        >
                                            {flattenCategories(categories)
                                                .filter(c => c.name.toLowerCase().includes(searchCategoryText.toLowerCase()) && c.id !== selectedCategory?.id)
                                                .map(c => (
                                                    <option key={c.id} value={c.id}>{c.name}</option>
                                                ))
                                            }
                                        </select>
                                    </div>
                                ) : (
                                    /* Normal Hiyerarşik Yapı: Ana Kategori -> Alt Kategori Select Kutuları */
                                    <>
                                        {/* 1. Ana Kategoriler */}
                                        <div className="mb-3">
                                            <label className="form-label small fw-bold">Ana Kategori Seçin</label>
                                            <select 
                                                className="form-select"
                                                value={selectedRootCatId}
                                                onChange={(e) => {
                                                    setSelectedRootCatId(e.target.value);
                                                    setSelectedSubCatId(""); // Alt kategoriyi sıfırla
                                                }}
                                            >
                                                <option value="">--- Ana Kategori Seçiniz ---</option>
                                                {categories.map(cat => (
                                                    <option key={cat.id} value={cat.id} disabled={cat.id === selectedCategory?.id}>
                                                        {cat.name} {cat.id === selectedCategory?.id ? "(Bulunduğu Kategori)" : ""}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* 2. Seçilen Ana Kategorinin Alt Kategorileri (Varsa) */}
                                        {selectedRootCatId && (() => {
                                            const parentCat = categories.find(c => c.id === Number(selectedRootCatId));
                                            const subCats = parentCat ? parentCat.children : [];
                                            if (subCats && subCats.length > 0) {
                                                return (
                                                    <div className="mb-3">
                                                        <label className="form-label small fw-bold">Alt Kategori Seçin (Opsiyonel)</label>
                                                        <select 
                                                            className="form-select"
                                                            value={selectedSubCatId}
                                                            onChange={(e) => setSelectedSubCatId(e.target.value)}
                                                        >
                                                            <option value="">--- Alt Kategori Yok / Ana Kategoride Kal ---</option>
                                                            {subCats.map(sub => (
                                                                <option key={sub.id} value={sub.id} disabled={sub.id === selectedCategory?.id}>
                                                                    {sub.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })()}
                                    </>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" disabled={isMoving} onClick={() => setMoveModalOpen(false)}>İptal</button>
                                <button 
                                    type="button" 
                                    className="btn btn-primary" 
                                    disabled={isMoving || (!selectedRootCatId && !selectedSubCatId)} 
                                    onClick={handleMoveMedias}
                                >
                                    {isMoving ? "Taşınıyor..." : "Taşıma İşlemini Tamamla"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* --- KATEGORİ MODALI --- */}
            {showCatModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <form onSubmit={handleSaveCategory}>
                                <div className="modal-header">
                                    <h5 className="modal-title">{catModalMode === "create" ? "Kategori Oluştur" : "Kategori Düzenle"}</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowCatModal(false)}></button>
                                </div>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Kategori Adı</label>
                                        <input 
                                            type="text" 
                                            className="form-control" 
                                            value={catForm.name} 
                                            onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} 
                                            required 
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Açıklama</label>
                                        <textarea 
                                            className="form-control" 
                                            value={catForm.description || ""} 
                                            onChange={(e) => setCatForm({ ...catForm, description: e.target.value })} 
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Sıra (Sort ID)</label>
                                        <input 
                                            type="number" 
                                            className="form-control" 
                                            value={catForm.sortid} 
                                            onChange={(e) => setCatForm({ ...catForm, sortid: e.target.value })} 
                                        />
                                    </div>
                                    <div className="mb-3">
    <label className="form-label">Üst Kategori (Opsiyonel)</label>
    <select 
        className="form-select"
        value={catForm.category_id || ""}
        onChange={(e) => setCatForm({ ...catForm, category_id: e.target.value ? Number(e.target.value) : null })}
    >
        <option value="">--- Ana Kategori (Üst Kategorisi Yok) ---</option>
        {flattenCategories(categories)
            .filter(c => editingCategory ? c.id !== editingCategory.id : true) // Düzenlemede kendini üst kategori seçmesini engelle
            .map(cat => (
                <option key={cat.id} value={cat.id}>
                    {cat.name}
                </option>
            ))
        }
    </select>
</div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowCatModal(false)}>İptal</button>
                                    <button type="submit" className="btn btn-primary">Kaydet</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* --- TOPLU YÜKLEME KUYRUK MODALI --- */}
            {uploadModalOpen && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <input type="file" ref={addMoreInputRef} style={{ display: "none" }} multiple onChange={handleAddMoreFiles} />
                            <input type="file" id="replaceFileInput" style={{ display: "none" }} onChange={handleReplaceFileChosen} />

                            <div className="modal-header d-flex justify-content-between align-items-center">
                                <h5 className="modal-title">Toplu Dosya Yükleme Kuyruğu ({activeUploadType.toUpperCase()})</h5>
                                {!isUploading && (
                                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => addMoreInputRef.current.click()}>
                                        ➕ Yeni Dosya Ekle
                                    </button>
                                )}
                            </div>
                            <div className="modal-body" style={{ maxHeight: "60vh", overflowY: "auto" }}>
                                {uploadQueue.map((item, idx) => (
                                    <div key={item.id} className="border p-2 rounded mb-2 bg-light">
                                        <div className="d-flex align-items-center gap-2">
                                            <div style={{ width: "30px" }} className="text-center fw-bold">
                                                {item.status === "pending" && "⏳"}
                                                {item.status === "uploading" && <div className="spinner-border spinner-border-sm text-primary" role="status"></div>}
                                                {item.status === "success" && "✅"}
                                                {item.status === "error" && (
                                                    <span 
                                                        className="text-danger fs-5" 
                                                        style={{ cursor: "pointer" }}
                                                        title="Hatalı! Değiştirmek için tıklayın"
                                                        onClick={() => {
                                                            if (isUploading) return;
                                                            replacingIndexRef.current = idx;
                                                            document.getElementById("replaceFileInput").click();
                                                        }}
                                                    >
                                                        ❌
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex-grow-1">
                                                <input 
                                                    type="text" 
                                                    className="form-control form-control-sm" 
                                                    value={item.name} 
                                                    disabled={isUploading || item.status === "success"}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        setUploadQueue(prev => prev.map((q, i) => i === idx ? { ...q, name: val } : q));
                                                    }}
                                                    placeholder="Dosya Adı"
                                                />
                                            </div>
                                            <div className="flex-grow-1">
                                                <input 
                                                    type="text" 
                                                    className="form-control form-control-sm" 
                                                    value={item.description} 
                                                    disabled={isUploading || item.status === "success"}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        setUploadQueue(prev => prev.map((q, i) => i === idx ? { ...q, description: val } : q));
                                                    }}
                                                    placeholder="Açıklama (Opsiyonel)"
                                                />
                                            </div>
                                            <div style={{ width: "130px" }} className="d-flex align-items-center justify-content-between">
                                                <span className="small text-muted text-truncate" title={item.file.name}>{item.file.name}</span>
                                                {!isUploading && item.status !== "success" && (
                                                    <button type="button" className="btn btn-sm btn-outline-danger py-0 px-1" onClick={() => handleRemoveFromQueue(idx)}>🗑️</button>
                                                )}
                                            </div>
                                        </div>
                                        {item.status === "error" && (
                                            <div className="text-danger small mt-1 ps-4">
                                                ⚠️ {item.errorMessage} (Değiştirmek için ❌ ikonuna tıklayın)
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" disabled={isUploading} onClick={() => setUploadModalOpen(false)}>Kapat</button>
                                <button type="button" className="btn btn-primary" disabled={isUploading || uploadQueue.every(q => q.status === "success")} onClick={startBatchUpload}>
                                    {isUploading ? "Yükleniyor..." : "Yüklemeyi Başlat / Kalanları Gönder"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* --- MEDYA ÖNİZLEME VE DÜZENLEME MODALI --- */}
            {previewMedia && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.8)" }} onClick={() => setPreviewMedia(null)}>
                    <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content bg-dark text-white border-0">
                            <div className="modal-header border-0">
                                <h5 className="modal-title">Medya Detayı & Düzenle</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setPreviewMedia(null)}></button>
                            </div>
                            <div className="modal-body p-3">
                                <div className="row">
                                    <div className="col-md-6 text-center d-flex align-items-center justify-content-center bg-black rounded p-2" style={{ minHeight: "250px" }}>
                                        {previewMedia.type === 1 || previewMedia.type === "resim" ? (
                                            <img src={previewMedia.url} alt={previewMedia.name} className="img-fluid rounded" style={{ maxHeight: "300px", objectFit: "contain" }} />
                                        ) : previewMedia.type === 3 || previewMedia.type === "video" ? (
                                            <video src={previewMedia.url} controls className="w-100 rounded" />
                                        ) : (
                                            <div className="p-5">
                                                <audio src={previewMedia.url} controls className="w-100" />
                                            </div>
                                        )}
                                    </div>

                                    <div className="col-md-6 d-flex flex-column justify-content-between">
                                        <form onSubmit={handleUpdateMedia}>
                                            <div className="mb-3">
                                                <label className="form-label text-light small">Medya Adı</label>
                                                <input 
                                                    type="text" 
                                                    className="form-control bg-secondary text-white border-0" 
                                                    value={editMediaForm.name} 
                                                    onChange={(e) => setEditMediaForm({ ...editMediaForm, name: e.target.value })} 
                                                    required 
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label text-light small">Açıklama</label>
                                                <textarea 
                                                    className="form-control bg-secondary text-white border-0" 
                                                    rows="4"
                                                    value={editMediaForm.description} 
                                                    onChange={(e) => setEditMediaForm({ ...editMediaForm, description: e.target.value })} 
                                                    placeholder="Medya açıklaması ekleyin..."
                                                />
                                            </div>
                                            <div className="d-flex justify-content-end gap-2">
                                                <button 
                                                    type="submit" 
                                                    className="btn btn-primary btn-sm px-4"
                                                    disabled={isUpdatingMedia}
                                                >
                                                    {isUpdatingMedia ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminMediaManagement;